import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import {
  Building2, PackageMinus, PackagePlus, Plus, RefreshCw, Inbox, CheckCircle2,
  Hourglass, XCircle, MapPin, Calendar as CalendarIcon, Check, ChevronsUpDown,
} from "lucide-react";
import SignaturePad from "./SignaturePad";
import { formatDateFr } from "../lib/dateFormat";

const REASON_OPTIONS = [
  { value: "usure",  label: "Usure" },
  { value: "defaut", label: "Défaut constaté" },
  { value: "perte",  label: "Perte" },
  { value: "autre",  label: "Autre" },
];

const STATUS_BADGE = {
  en_attente: { label: "En attente", Icon: Hourglass, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approuve:   { label: "Approuvée", Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  refuse:     { label: "Refusée",   Icon: XCircle, cls: "bg-red-50 text-red-700 border-red-200" },
};

/**
 * Section "Demandes sites" du dossier personnel d'un collaborateur.
 * Permet d'initier une demande de matériel pour un site où il a un shift planifié
 * dans les 7 prochains jours. Deux types :
 *  - Remplacement d'un matériel déjà affecté au site
 *  - Nouveau besoin (rédaction libre, sans catalogue)
 */
export default function MesDemandesSites() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/equipment-site-requests");
      setRequests(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement des demandes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { en_attente: 0, approuve: 0, refuse: 0 };
    for (const r of requests) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [requests]);

  return (
    <div className="space-y-4" data-testid="mes-demandes-sites">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={14} className="text-zinc-500" />
            <p className="label-uppercase">Demandes sites</p>
          </div>
          <h2 className="text-lg sm:text-xl font-display font-semibold text-zinc-900">
            Demandes de matériel pour vos sites
          </h2>
          <p className="text-xs text-zinc-600 font-mono mt-1">
            {counts.en_attente} en attente · {counts.approuve} approuvée(s) · {counts.refuse} refusée(s)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button" variant="outline" size="sm"
            onClick={load} disabled={loading}
            data-testid="site-req-refresh"
          >
            <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </Button>
          <Button
            type="button" size="sm"
            onClick={() => setOpenCreate(true)}
            className="bg-[#0055FF] hover:bg-[#0044CC]"
            data-testid="site-req-new"
          >
            <Plus size={13} className="mr-1.5" /> Nouvelle demande
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        {loading && requests.length === 0 ? (
          <p className="text-sm text-zinc-500 font-mono py-10 text-center">Chargement…</p>
        ) : requests.length === 0 ? (
          <div className="py-12 text-center" data-testid="site-req-empty">
            <Inbox className="mx-auto text-zinc-300 mb-3" size={36} />
            <p className="text-sm text-zinc-500 font-mono">
              Aucune demande site pour le moment.
            </p>
            <p className="text-xs text-zinc-400 font-mono mt-1">
              Vous pouvez créer une demande pour un site où vous avez un shift dans les 7 prochains jours.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {requests.map((r) => {
              const b = STATUS_BADGE[r.status] || STATUS_BADGE.en_attente;
              const StatusIcon = b.Icon;
              const isRempl = r.request_type === "remplacement";
              return (
                <div key={r.id} className="p-4 flex items-start gap-3 flex-wrap" data-testid={`site-req-row-${r.id}`}>
                  <span className="flex-shrink-0 mt-0.5">
                    {isRempl
                      ? <PackageMinus size={18} className="text-amber-600" />
                      : <PackagePlus size={18} className="text-sky-600" />}
                  </span>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-zinc-50 border-zinc-200 text-zinc-700">
                        {isRempl ? "Remplacement" : "Nouveau besoin"}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider ${b.cls}`}>
                        <StatusIcon size={10} /> {b.label}
                      </span>
                      {r.status === "approuve" && r.stock_pending && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-amber-50 border-amber-300 text-amber-800"
                          title="Approbation enregistrée par le manager, mais aucun matériel ne vous a encore été affecté (stock insuffisant)."
                          data-testid={`mes-site-req-stock-pending-${r.id}`}
                        >
                          Stock en attente
                        </span>
                      )}
                      <p className="text-sm font-medium text-zinc-900 inline-flex items-center gap-1">
                        <MapPin size={12} className="text-zinc-400" />{r.site_name || "—"}
                      </p>
                    </div>
                    <p className="text-sm text-zinc-700">
                      {isRempl
                        ? <>Remplacement de <b>{r.designation_snapshot || "—"}</b> ({r.ref_id_snapshot || "—"}) · qté <b>{r.quantite_demandee}</b></>
                        : <>{r.besoin_titre} · qté <b>{r.quantite_demandee}</b></>}
                    </p>
                    {isRempl && r.reason_type && (
                      <p className="text-xs text-zinc-500 font-mono mt-0.5">
                        Motif : {REASON_OPTIONS.find(o => o.value === r.reason_type)?.label || r.reason_type}
                        {r.reason ? ` — ${r.reason}` : ""}
                      </p>
                    )}
                    {!isRempl && r.besoin_description && (
                      <p className="text-xs text-zinc-500 font-mono mt-0.5">{r.besoin_description}</p>
                    )}
                    <p className="text-[11px] text-zinc-400 font-mono mt-1">
                      <CalendarIcon size={10} className="inline mr-1" />
                      Émise le {formatDateFr(r.created_at.slice(0, 10))}
                      {r.decided_at && r.status !== "en_attente" && (
                        <> · Décidée le {formatDateFr(r.decided_at.slice(0, 10))} par {r.decided_by_name}</>
                      )}
                    </p>
                    {r.status === "refuse" && r.decision_note && (
                      <p className="text-xs text-red-700 mt-1">Motif refus : {r.decision_note}</p>
                    )}
                    {r.status === "approuve" && r.receiver_name && r.receiver_id !== user?.id && (
                      <p className="text-xs text-sky-700 mt-1">
                        Réception sur site confiée à <b>{r.receiver_name}</b>.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <NewSiteRequestDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={() => { setOpenCreate(false); load(); }}
      />
    </div>
  );
}

/* ------------------------- Modal de création ------------------------- */

export function NewSiteRequestDialog({ open, onClose, onCreated }) {
  const [tab, setTab] = useState("remplacement");
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [sitePopoverOpen, setSitePopoverOpen] = useState(false);
  const [siteAssignments, setSiteAssignments] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form remplacement
  const [siteAssignId, setSiteAssignId] = useState("");
  const [reasonType, setReasonType] = useState("usure");
  const [reason, setReason] = useState("");
  const [qty, setQty] = useState(1);

  // Form nouveau besoin
  const [besoinTitre, setBesoinTitre] = useState("");
  const [besoinDescription, setBesoinDescription] = useState("");
  const [suggestionUrl, setSuggestionUrl] = useState("");

  const [signature, setSignature] = useState(null);

  // Reset à l'ouverture
  useEffect(() => {
    if (!open) return;
    setTab("remplacement");
    setSiteId(""); setSiteAssignments([]); setSiteAssignId("");
    setReasonType("usure"); setReason(""); setQty(1);
    setBesoinTitre(""); setBesoinDescription(""); setSuggestionUrl("");
    setSignature(null);
    (async () => {
      setLoadingSites(true);
      try {
        // On charge désormais TOUS les sites des départements du collaborateur
        // (et non plus uniquement les sites planifiés à 7j). Le filtre par
        // brands est appliqué côté backend selon le rôle de l'utilisateur.
        const r = await api.get("/sites");
        setSites(r.data || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Impossible de charger la liste des sites");
      } finally {
        setLoadingSites(false);
      }
    })();
  }, [open]);

  // Quand le site change, charger les matériels affectés à ce site
  useEffect(() => {
    if (!siteId || tab !== "remplacement") {
      setSiteAssignments([]); setSiteAssignId("");
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/equipment-site-assignments/by-site/${siteId}`);
        setSiteAssignments(r.data || []);
        setSiteAssignId("");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur chargement matériels du site");
      }
    })();
  }, [siteId, tab]);

  const canSubmit = useMemo(() => {
    if (!siteId || !signature) return false;
    if (tab === "remplacement") {
      return !!siteAssignId && qty >= 1 && !!reasonType;
    }
    return besoinTitre.trim().length > 0 && qty >= 1;
  }, [siteId, signature, tab, siteAssignId, qty, reasonType, besoinTitre]);

  const submit = async () => {
    if (!canSubmit) return;
    const payload = {
      site_id: siteId,
      request_type: tab,
      requester_signature: signature,
      quantite_demandee: Number(qty) || 1,
    };
    if (tab === "remplacement") {
      payload.site_assignment_id = siteAssignId;
      payload.reason_type = reasonType;
      payload.reason = reason;
    } else {
      payload.besoin_titre = besoinTitre.trim();
      payload.besoin_description = besoinDescription;
      payload.suggestion_url = suggestionUrl;
    }
    try {
      setSubmitting(true);
      await api.post("/equipment-site-requests", payload);
      toast.success("Demande envoyée — le manager va être notifié");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="flex flex-col max-h-[90vh] p-0 sm:max-w-2xl"
        data-testid="site-req-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
          <DialogTitle>Nouvelle demande pour un site</DialogTitle>
          <DialogDescription>
            Choisissez le site concerné, puis le type de demande.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
          <div>
            <Label className="label-uppercase">Site concerné</Label>
            <Popover open={sitePopoverOpen} onOpenChange={setSitePopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  role="combobox"
                  aria-expanded={sitePopoverOpen}
                  data-testid="site-req-site-select"
                  className="mt-1 w-full flex items-center justify-between rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-left hover:bg-zinc-50 disabled:opacity-50"
                >
                  <span className={siteId ? "text-zinc-900" : "text-zinc-500"}>
                    {loadingSites
                      ? "Chargement…"
                      : siteId
                        ? (sites.find((s) => s.id === siteId)?.name || "Site sélectionné")
                        : "Sélectionnez ou recherchez un site"}
                  </span>
                  <ChevronsUpDown size={14} className="text-zinc-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
                <Command>
                  <CommandInput placeholder="Rechercher un site…" data-testid="site-req-site-search" />
                  <CommandList>
                    <CommandEmpty>Aucun site trouvé.</CommandEmpty>
                    <CommandGroup>
                      {sites.map((s) => (
                        <CommandItem
                          key={s.id}
                          value={`${s.name} ${s.address || ""} ${s.city || ""}`}
                          onSelect={() => { setSiteId(s.id); setSitePopoverOpen(false); }}
                          data-testid={`site-req-site-${s.id}`}
                        >
                          <Check size={13} className={`mr-2 ${siteId === s.id ? "opacity-100" : "opacity-0"}`} />
                          <span className="flex-1">
                            <span className="font-medium">{s.name}</span>
                            {(s.address || s.city) && (
                              <span className="block text-[11px] text-zinc-500 font-mono">
                                {[s.address, s.city].filter(Boolean).join(" · ")}
                              </span>
                            )}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-1 sm:grid-cols-2 w-full h-auto gap-1">
              <TabsTrigger value="remplacement" className="py-1.5 justify-center whitespace-normal text-xs sm:text-sm" data-testid="site-req-tab-replace">
                <PackageMinus size={13} className="mr-1.5 shrink-0" /> Remplacement matériels
              </TabsTrigger>
              <TabsTrigger value="nouveau_besoin" className="py-1.5 justify-center whitespace-normal text-xs sm:text-sm" data-testid="site-req-tab-new">
                <PackagePlus size={13} className="mr-1.5 shrink-0" /> Nouveau besoin
              </TabsTrigger>
            </TabsList>

            <TabsContent value="remplacement" className="space-y-3 pt-3">
              {siteId && siteAssignments.length === 0 ? (
                <p className="text-sm text-zinc-500 font-mono italic">
                  Aucun matériel actuellement affecté à ce site. Utilisez l'onglet « Nouveau besoin ».
                </p>
              ) : (
                <>
                  <div>
                    <Label className="label-uppercase">Matériel à remplacer</Label>
                    <Select value={siteAssignId} onValueChange={setSiteAssignId} disabled={!siteId}>
                      <SelectTrigger data-testid="site-req-assign-select" className="mt-1">
                        <SelectValue placeholder={siteId ? "Sélectionnez le matériel" : "Choisissez d'abord un site"} />
                      </SelectTrigger>
                      <SelectContent>
                        {siteAssignments.map((a) => (
                          <SelectItem key={a.id} value={a.id} data-testid={`site-req-assign-${a.id}`}>
                            {a.designation_snapshot} ({a.ref_id_snapshot}) — qté {a.quantite_sortie} — {a.etat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="label-uppercase">Motif</Label>
                      <Select value={reasonType} onValueChange={setReasonType}>
                        <SelectTrigger className="mt-1" data-testid="site-req-reason">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {REASON_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="label-uppercase">Quantité demandée</Label>
                      <Input
                        type="number" min={1} value={qty}
                        onChange={(e) => setQty(e.target.value)}
                        className="mt-1"
                        data-testid="site-req-qty-replace"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="label-uppercase">Précisions (optionnel)</Label>
                    <Textarea
                      value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Détail du défaut, contexte d'utilisation, etc."
                      className="mt-1" rows={2}
                      data-testid="site-req-reason-detail"
                    />
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="nouveau_besoin" className="space-y-3 pt-3">
              <div>
                <Label className="label-uppercase">Désignation du besoin</Label>
                <Input
                  value={besoinTitre}
                  onChange={(e) => setBesoinTitre(e.target.value)}
                  placeholder="Ex. : Imprimante laser réseau, plateau de service…"
                  className="mt-1"
                  data-testid="site-req-besoin-titre"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="label-uppercase">Suggestion / lien produit (optionnel)</Label>
                  <Input
                    value={suggestionUrl}
                    onChange={(e) => setSuggestionUrl(e.target.value)}
                    placeholder="https://…"
                    className="mt-1"
                    data-testid="site-req-suggestion-url"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Quantité estimée</Label>
                  <Input
                    type="number" min={1} value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    className="mt-1"
                    data-testid="site-req-qty-new"
                  />
                </div>
              </div>
              <div>
                <Label className="label-uppercase">Description du besoin</Label>
                <Textarea
                  value={besoinDescription}
                  onChange={(e) => setBesoinDescription(e.target.value)}
                  placeholder="Décrivez précisément le besoin, le contexte d'utilisation et l'urgence."
                  className="mt-1" rows={3}
                  data-testid="site-req-besoin-desc"
                />
              </div>
            </TabsContent>
          </Tabs>

          <div>
            <Label className="label-uppercase">Votre signature</Label>
            <p className="text-xs text-zinc-500 font-mono mb-1">
              Engage votre nom auprès du manager pour cette demande.
            </p>
            <SignaturePad onSave={setSignature} value={signature} />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="site-req-cancel">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="bg-[#0055FF] hover:bg-[#0044CC]"
            data-testid="site-req-submit"
          >
            {submitting ? "Envoi…" : "Envoyer la demande"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
